import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { usersAPI } from "../../services/api";

export interface User {
	_id: string;
	username: string;
	email: string;
	role: string;
	isActive: boolean;
	createdAt: string;
}

export interface UserStats {
	total: number;
	active: number;
	admins: number;
	managers: number;
}

interface UsersState {
	users: User[];
	loading: boolean;
	error: string | null;
	total: number;
	page: number;
	limit: number;
	// Id of the most recent list request, so a slower earlier one cannot
	// overwrite it: a delete refreshes the current page while the page number is
	// being corrected, leaving two fetches in flight for different pages.
	latestFetchRequestId: string | null;
	// Counted over every user, not the page of them on screen
	stats: UserStats;
	// The same counts under the page's filters, null while unfiltered
	filteredStats: UserStats | null;
	latestFilteredStatsRequestId: string | null;
}

const initialState: UsersState = {
	users: [],
	loading: false,
	error: null,
	total: 0,
	page: 1,
	limit: 10,
	latestFetchRequestId: null,
	stats: { total: 0, active: 0, admins: 0, managers: 0 },
	filteredStats: null,
	latestFilteredStatsRequestId: null,
};

// Async thunks
export const fetchUsers = createAsyncThunk(
	"users/fetchUsers",
	async (
		params: {
			page?: number;
			limit?: number;
			search?: string;
			role?: string;
			sortBy?: string;
			sortOrder?: string;
		} = {},
		{ rejectWithValue }
	) => {
		try {
			const response = await usersAPI.getAll(params);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to fetch users"
				);
			}
			return rejectWithValue("Failed to fetch users");
		}
	}
);

export const fetchFilteredUserStats = createAsyncThunk(
	"users/fetchFilteredUserStats",
	async (filters: { search?: string; role?: string }, { rejectWithValue }) => {
		try {
			const response = await usersAPI.getStats(filters);
			return response.data.data as UserStats;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to fetch filtered stats"
				);
			}
			return rejectWithValue("Failed to fetch filtered stats");
		}
	}
);

export const fetchUserStats = createAsyncThunk(
	"users/fetchUserStats",
	async (_params: void, { rejectWithValue }) => {
		try {
			const response = await usersAPI.getStats();
			return response.data.data as UserStats;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to fetch user stats"
				);
			}
			return rejectWithValue("Failed to fetch user stats");
		}
	}
);

export const createUser = createAsyncThunk(
	"users/createUser",
	async (
		userData: {
			username: string;
			email: string;
			password: string;
			role: string;
		},
		{ rejectWithValue }
	) => {
		try {
			const response = await usersAPI.create(userData);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to create user"
				);
			}
			return rejectWithValue("Failed to create user");
		}
	}
);

export const updateUser = createAsyncThunk(
	"users/updateUser",
	async (
		{
			id,
			...userData
		}: {
			id: string;
			username: string;
			email: string;
			role: string;
			isActive: boolean;
		},
		{ rejectWithValue }
	) => {
		try {
			const response = await usersAPI.update(id, userData);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to update user"
				);
			}
			return rejectWithValue("Failed to update user");
		}
	}
);

export const deleteUser = createAsyncThunk(
	"users/deleteUser",
	async (id: string, { rejectWithValue }) => {
		try {
			await usersAPI.delete(id);
			return id;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to delete user"
				);
			}
			return rejectWithValue("Failed to delete user");
		}
	}
);

const usersSlice = createSlice({
	name: "users",
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		clearFilteredStats: (state) => {
			state.filteredStats = null;
			state.latestFilteredStatsRequestId = null;
		},
		setPage: (state, action: PayloadAction<number>) => {
			state.page = action.payload;
		},
		setLimit: (state, action: PayloadAction<number>) => {
			state.limit = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch users
			.addCase(fetchUsers.pending, (state, action) => {
				state.loading = true;
				state.error = null;
				state.latestFetchRequestId = action.meta.requestId;
			})
			.addCase(fetchUsers.fulfilled, (state, action) => {
				if (state.latestFetchRequestId !== action.meta.requestId) return;
				state.loading = false;
				const response = action.payload as unknown as {
					data: { users: User[]; total: number };
				};
				state.users = response.data?.users || [];
				state.total = response.data?.total || 0;
			})
			.addCase(fetchUsers.rejected, (state, action) => {
				if (state.latestFetchRequestId !== action.meta.requestId) return;
				state.loading = false;
				state.error = action.payload as string;
			})
			// Filters change as fast as the user types; an older response must
			// not replace the counts for what is on screen now
			.addCase(fetchFilteredUserStats.pending, (state, action) => {
				state.latestFilteredStatsRequestId = action.meta.requestId;
			})
			.addCase(fetchFilteredUserStats.fulfilled, (state, action) => {
				if (state.latestFilteredStatsRequestId !== action.meta.requestId) return;
				state.filteredStats = action.payload;
			})
			.addCase(fetchFilteredUserStats.rejected, (state, action) => {
				if (state.latestFilteredStatsRequestId !== action.meta.requestId) return;
				state.filteredStats = null;
				state.error = action.payload as string;
			})
			.addCase(fetchUserStats.fulfilled, (state, action) => {
				state.stats = action.payload;
			})
			.addCase(fetchUserStats.rejected, (state, action) => {
				state.error = action.payload as string;
			})
			// Create user
			.addCase(createUser.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createUser.fulfilled, (state, action) => {
				state.loading = false;
				const response = action.payload as unknown as {
					data: { user: { id: string } & Omit<User, "_id"> };
				};
				if (response.data?.user) {
					const userData = response.data.user;
					// Backend returns 'id' but frontend uses '_id'
					state.users.unshift({
						_id: userData.id,
						username: userData.username,
						email: userData.email,
						role: userData.role,
						isActive: userData.isActive,
						createdAt: userData.createdAt,
					});
					state.total += 1;
				}
			})
			.addCase(createUser.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Update user
			.addCase(updateUser.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateUser.fulfilled, (state, action) => {
				state.loading = false;
				const response = action.payload as unknown as {
					data: { user: { id: string } & Omit<User, "_id"> };
				};
				if (response.data?.user) {
					const userData = response.data.user;
					// Backend returns 'id' but frontend uses '_id'
					const userId = userData.id;
					const index = state.users.findIndex((user) => user._id === userId);
					if (index !== -1) {
						state.users[index] = {
							...state.users[index],
							_id: userId,
							username: userData.username,
							email: userData.email,
							role: userData.role,
							isActive: userData.isActive,
						};
					}
				}
			})
			.addCase(updateUser.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Delete user
			.addCase(deleteUser.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteUser.fulfilled, (state, action) => {
				state.loading = false;
				state.users = state.users.filter((user) => user._id !== action.payload);
				state.total -= 1;
			})
			.addCase(deleteUser.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			});
	},
});

export const { clearError, clearFilteredStats, setPage, setLimit } = usersSlice.actions;
export default usersSlice.reducer;
